// ---------------------------------------------------------------------------
// The login screen.
//
// Two ways in:
//   * username and password, for everybody,
//   * a fast PIN pad, for employees who have been given a PIN - the usual
//     route for a cashier starting a shift on a touchscreen.
//
// Deliberately uncrowded: the logo, one panel, and the footer. Nothing else
// competes for attention at the start of a shift.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Delete, Eye, EyeOff, KeyRound, Lock, User, WifiOff } from 'lucide-react';
import clsx from 'clsx';
import { useAuthStore } from '../stores/authStore';
import { useI18n } from '../i18n/I18nProvider';
import { useClock } from '../hooks/useClock';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { usePinUsers } from '../api/system.api';
import { ApiError } from '../lib/apiClient';
import { landingPathFor, NAV_ITEMS } from '../routes/navigation';
import { formatLongDate, formatTime } from '../utils/format';
import { YaghiLogo } from '../components/common/YaghiLogo';
import { Spinner } from '../components/common/Spinner';
import { LanguageSwitcher } from '../components/common/LanguageSwitcher';

type Mode = 'password' | 'pin';

const passwordSchema = z.object({
  username: z.string().trim().min(1, 'required'),
  password: z.string().min(1, 'required'),
});

type PasswordForm = z.infer<typeof passwordSchema>;

export function LoginPage() {
  const { t, locale, format } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const now = useClock();
  const online = useOnlineStatus();

  useDocumentTitle(t.login.welcome);

  const loginWithPassword = useAuthStore((state) => state.loginWithPassword);
  const loginWithPin = useAuthStore((state) => state.loginWithPin);
  const sessionEndedMessage = useAuthStore((state) => state.sessionEndedMessage);
  const clearSessionEndedMessage = useAuthStore((state) => state.clearSessionEndedMessage);

  const [mode, setMode] = useState<Mode>('password');
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState<ApiError | null>(null);
  /// When a temporary lock ends, as a timestamp on THIS device.
  ///
  /// Informational only. The server judges every attempt against its own
  /// `locked_until`, so a fast clock, a paused tab or an edited number here
  /// gets nobody in early - it only decides what the screen says and when the
  /// warning clears itself.
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);

  // PIN state
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [pinSubmitting, setPinSubmitting] = useState(false);

  // Started the moment the page mounts, whichever tab is showing. The two
  // tabs load independently: neither waits for the other, and neither waits
  // for the application's own bootstrap.
  const {
    data: pinData,
    isPending: pinUsersPending,
    isError: pinUsersFailed,
    refetch: refetchPinUsers,
  } = usePinUsers();
  const pinUsers = pinData?.users ?? [];
  const selectedUser = pinUsers.find((user) => user.id === selectedUserId) ?? null;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<PasswordForm>({
    resolver: zodResolver(passwordSchema),
    defaultValues: { username: '', password: '' },
  });

  const goAfterLogin = () => {
    const permissions = useAuthStore.getState().permissions;
    const from = (location.state as { from?: string } | null)?.from;

    // Only honour the remembered destination if this person may actually open
    // it - otherwise they would be bounced straight to the refusal page.
    const target = from && permissionAllowsPath(from, permissions) ? from : landingPathFor(permissions);
    navigate(target, { replace: true });
  };

  /// Show a failed sign-in, and start the countdown if it was a lock.
  ///
  /// The server sends the seconds remaining as `meta.retryAfterSeconds`, so
  /// the screen never has to parse its own sentence to find the number.
  const showFailure = (error: unknown) => {
    const apiError =
      error instanceof ApiError ? error : new ApiError(0, 'INTERNAL_ERROR', t.errors.unexpectedBody);
    setFormError(apiError);

    if (apiError.code === 'ACCOUNT_LOCKED') {
      const seconds = Number(apiError.meta?.retryAfterSeconds ?? 0);
      setLockedUntil(seconds > 0 ? Date.now() + seconds * 1000 : null);
    } else {
      setLockedUntil(null);
    }
  };

  const onPasswordSubmit = handleSubmit(async (values) => {
    setFormError(null);
    clearSessionEndedMessage();
    try {
      await loginWithPassword(values.username, values.password);
      goAfterLogin();
    } catch (error) {
      showFailure(error);
    }
  });

  // The PIN submits itself as soon as it is long enough - one less tap.
  useEffect(() => {
    if (mode !== 'pin' || !selectedUserId || pin.length < 4 || pinSubmitting || locked) return;

    let cancelled = false;
    const submit = async () => {
      setPinSubmitting(true);
      setFormError(null);
      clearSessionEndedMessage();
      try {
        await loginWithPin(selectedUserId, pin);
        if (!cancelled) goAfterLogin();
      } catch (error) {
        if (cancelled) return;
        setPin('');
        showFailure(error);
      } finally {
        if (!cancelled) setPinSubmitting(false);
      }
    };

    void submit();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pin, selectedUserId, mode]);

  const switchMode = (next: Mode) => {
    setMode(next);
    setFormError(null);
    setPin('');
    setSelectedUserId(null);
  };

  // --- the lock countdown --------------------------------------------------
  //
  // A timer of its own, and ONLY while a lock is running. `useClock` looks
  // like the obvious source and is not: it re-renders when the MINUTE
  // changes, which is right for a wall clock and useless for counting thirty
  // seconds - the number would sit still and then jump.
  //
  // No polling either. The server already said how long to wait, so the
  // screen counts locally and clears itself at zero; the next attempt is
  // judged by the server regardless of what this counter did.
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (lockedUntil === null) return;

    const id = setInterval(() => setTick((value) => value + 1), 250);
    return () => clearInterval(id);
  }, [lockedUntil]);

  const secondsLeft =
    lockedUntil === null ? 0 : Math.max(0, Math.ceil((lockedUntil - Date.now()) / 1000));

  useEffect(() => {
    if (lockedUntil !== null && secondsLeft === 0) {
      setLockedUntil(null);
      setFormError(null);
    }
    // `tick` is what brings this back once a quarter-second while a lock runs.
  }, [lockedUntil, secondsLeft, tick]);

  const locked = formError?.code === 'ACCOUNT_LOCKED' && secondsLeft > 0;

  const errorTitle =
    formError?.code === 'ACCOUNT_SUSPENDED'
      ? t.login.suspendedTitle
      : formError?.code === 'ACCOUNT_LOCKED'
        ? t.login.lockedTitle
        : null;

  /// What the warning says. A locked account counts down in the cashier's own
  /// language; everything else shows the server's sentence as it always has.
  const errorBody = locked
    ? secondsLeft === 1
      ? t.login.lockedBodyOne
      : format(t.login.lockedBody, { seconds: secondsLeft })
    : (formError?.message ?? '');

  return (
    <div className="relative flex min-h-screen flex-col bg-surface-page">
      {/* A restrained brand band. No heavy gradients. */}
      <div className="absolute inset-x-0 top-0 h-1.5 bg-butcher-green" aria-hidden="true">
        <div className="mx-auto h-full max-w-md bg-antique-gold" />
      </div>

      <div className="absolute end-4 top-6 sm:end-6">
        <LanguageSwitcher />
      </div>

      <div className="flex flex-1 items-center justify-center px-4 py-14">
        <div className="w-full max-w-md">
          {/* --- brand --------------------------------------------------- */}
          <div className="mb-7 flex flex-col items-center text-center">
            <YaghiLogo height={72} />
            <h1 className="mt-5 text-xl font-semibold text-ink">{t.login.welcome}</h1>
            <p className="mt-1 text-sm text-ink-muted">{t.login.subtitle}</p>
          </div>

          {/* --- card ---------------------------------------------------- */}
          <div className="card p-6 sm:p-7">
            <p className="mb-5 text-center text-sm text-ink-muted">{t.login.tagline}</p>

            {/* --- mode switch ------------------------------------------- */}
            <div
              className="mb-5 grid grid-cols-2 gap-1 rounded-control bg-surface-sunken p-1"
              role="tablist"
              aria-label={t.login.submit}
            >
              {(['password', 'pin'] as const).map((option) => (
                <button
                  key={option}
                  type="button"
                  role="tab"
                  aria-selected={mode === option}
                  onClick={() => switchMode(option)}
                  className={clsx(
                    'flex items-center justify-center gap-2 rounded-[6px] py-2 text-sm font-semibold transition-colors',
                    mode === option ? 'bg-surface text-butcher-green shadow-sm' : 'text-ink-muted hover:text-ink',
                  )}
                >
                  {option === 'password' ? (
                    <Lock className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <KeyRound className="h-4 w-4" aria-hidden="true" />
                  )}
                  {option === 'password' ? t.login.withPassword : t.login.withPin}
                </button>
              ))}
            </div>

            {/* --- notices ----------------------------------------------- */}
            {sessionEndedMessage ? (
              <div
                className="mb-4 flex gap-2.5 rounded-control border border-antique-gold/40 bg-antique-gold/10 p-3 text-sm text-ink"
                role="status"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-antique-gold" aria-hidden="true" />
                <div>
                  <p className="font-semibold">{t.errors.sessionExpiredTitle}</p>
                  <p className="mt-0.5 text-ink-muted">{t.errors.sessionExpiredBody}</p>
                </div>
              </div>
            ) : null}

            {!online ? (
              <div
                className="mb-4 flex gap-2.5 rounded-control border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm"
                role="alert"
              >
                <WifiOff className="mt-0.5 h-4 w-4 shrink-0 text-burgundy-red" aria-hidden="true" />
                <p className="text-ink">{t.login.offlineWarning}</p>
              </div>
            ) : null}

            {formError ? (
              <div
                className="mb-4 flex gap-2.5 rounded-control border border-burgundy-red/30 bg-burgundy-red/10 p-3 text-sm"
                role="alert"
                aria-live="assertive"
              >
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-burgundy-red" aria-hidden="true" />
                <div>
                  {errorTitle ? <p className="font-semibold text-burgundy-red">{errorTitle}</p> : null}
                  <p className={clsx('text-ink', errorTitle && 'mt-0.5')}>{errorBody}</p>
                </div>
              </div>
            ) : null}

            {/* --- password form ----------------------------------------- */}
            {mode === 'password' ? (
              <form onSubmit={onPasswordSubmit} noValidate>
                <div className="mb-4">
                  <label htmlFor="username" className="label">
                    {t.login.usernameLabel}
                  </label>
                  <div className="relative">
                    <User
                      className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-muted"
                      aria-hidden="true"
                    />
                    <input
                      id="username"
                      type="text"
                      autoComplete="username"
                      autoFocus
                      dir="ltr"
                      placeholder={t.login.usernamePlaceholder}
                      className={clsx('input ps-10', errors.username && 'input-error')}
                      aria-invalid={Boolean(errors.username)}
                      aria-describedby={errors.username ? 'username-error' : undefined}
                      {...register('username')}
                    />
                  </div>
                  {errors.username ? (
                    <p id="username-error" className="field-error">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {t.login.usernameRequired}
                    </p>
                  ) : null}
                </div>

                <div className="mb-5">
                  <label htmlFor="password" className="label">
                    {t.login.passwordLabel}
                  </label>
                  <div className="relative">
                    <Lock
                      className="pointer-events-none absolute inset-y-0 start-3 my-auto h-4 w-4 text-ink-muted"
                      aria-hidden="true"
                    />
                    <input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      dir="ltr"
                      placeholder={t.login.passwordPlaceholder}
                      className={clsx('input ps-10 pe-11', errors.password && 'input-error')}
                      aria-invalid={Boolean(errors.password)}
                      aria-describedby={errors.password ? 'password-error' : undefined}
                      {...register('password')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((shown) => !shown)}
                      className="absolute inset-y-0 end-0 flex w-11 items-center justify-center text-ink-muted hover:text-ink"
                      aria-label={showPassword ? t.login.hidePassword : t.login.showPassword}
                    >
                      {showPassword ? (
                        <EyeOff className="h-4 w-4" aria-hidden="true" />
                      ) : (
                        <Eye className="h-4 w-4" aria-hidden="true" />
                      )}
                    </button>
                  </div>
                  {errors.password ? (
                    <p id="password-error" className="field-error">
                      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {t.login.passwordRequired}
                    </p>
                  ) : null}
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || locked}
                  className="btn-primary h-touch w-full"
                >
                  {isSubmitting ? <Spinner size="sm" /> : null}
                  {isSubmitting ? t.login.submitting : t.login.submit}
                </button>
              </form>
            ) : null}

            {/* --- PIN pad ------------------------------------------------ */}
            {mode === 'pin' ? (
              <div>
                {/* THE ORDER OF THESE THREE MATTERS, and getting it wrong is
                    what showed "no employee has a PIN" to a shop with four.
                    Pending first, then failed, and only then empty - an empty
                    list is a FACT the server stated, not the absence of an
                    answer. */}
                {pinUsersPending ? (
                  <div
                    className="flex flex-col items-center justify-center gap-3 py-8 text-butcher-green"
                    role="status"
                    aria-live="polite"
                  >
                    <Spinner size="lg" label={t.login.loadingEmployees} />
                    <span className="text-sm text-ink-muted">{t.login.loadingEmployees}</span>
                  </div>
                ) : pinUsersFailed ? (
                  <div className="py-6 text-center" role="alert">
                    <p className="text-sm text-ink-muted">{t.login.employeesFailed}</p>
                    <button
                      type="button"
                      className="btn-secondary mt-3"
                      onClick={() => void refetchPinUsers()}
                    >
                      {t.common.retry}
                    </button>
                  </div>
                ) : pinUsers.length === 0 ? (
                  <p className="py-6 text-center text-sm text-ink-muted" role="status">
                    {t.login.noPinUsers}
                  </p>
                ) : !selectedUser ? (
                  <>
                    <p className="mb-3 text-sm font-medium text-ink">{t.login.choosePerson}</p>
                    <div className="grid gap-2">
                      {pinUsers.map((user) => (
                        <button
                          key={user.id}
                          type="button"
                          onClick={() => setSelectedUserId(user.id)}
                          className="flex h-touch items-center gap-3 rounded-control border border-line-strong bg-surface px-4 text-start transition-colors hover:border-butcher-green hover:bg-surface-soft"
                        >
                          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-butcher-green text-sm font-semibold text-white">
                            {user.fullName.trim().charAt(0).toUpperCase()}
                          </span>
                          <span className="min-w-0">
                            <span className="block truncate text-sm font-semibold text-ink">
                              {user.fullName}
                            </span>
                            <span className="block truncate text-xs text-ink-muted">
                              {locale === 'ar' ? user.roleNameAr : user.roleNameEn}
                            </span>
                          </span>
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="mb-1 text-center text-sm font-medium text-ink">
                      {format(t.login.pinFor, { name: selectedUser.fullName })}
                    </p>
                    <p className="mb-4 text-center text-xs text-ink-muted">{t.login.enterPin}</p>

                    {/* Filled dots, so the PIN length is visible but the PIN is not. */}
                    <div className="mb-5 flex justify-center gap-2.5" aria-hidden="true">
                      {Array.from({ length: 4 }).map((_, index) => (
                        <span
                          key={index}
                          className={clsx(
                            'h-3.5 w-3.5 rounded-full border-2 transition-colors',
                            index < pin.length
                              ? 'border-butcher-green bg-butcher-green'
                              : 'border-line-strong bg-transparent',
                          )}
                        />
                      ))}
                    </div>
                    <span className="sr-only" aria-live="polite">
                      {pin.length} digits entered
                    </span>

                    {/* A keypad is a physical layout, not text: 1-2-3 runs left to right in every language, like a phone. */}
                    <div dir="ltr" className="grid grid-cols-3 gap-2" data-testid="pin-keypad">
                      {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((digit) => (
                        <button
                          key={digit}
                          type="button"
                          disabled={pinSubmitting}
                          onClick={() => setPin((current) => (current.length < 8 ? current + digit : current))}
                          className="numeric h-touch-lg rounded-control border border-line-strong bg-surface text-xl font-semibold text-ink transition-colors hover:bg-surface-sunken active:bg-surface-sunken disabled:opacity-50"
                        >
                          {digit}
                        </button>
                      ))}

                      <button
                        type="button"
                        onClick={() => setPin('')}
                        disabled={pinSubmitting}
                        className="h-touch-lg rounded-control border border-line-strong bg-surface text-sm font-medium text-ink-muted hover:bg-surface-sunken disabled:opacity-50"
                      >
                        {t.login.clear}
                      </button>

                      <button
                        type="button"
                        disabled={pinSubmitting}
                        onClick={() => setPin((current) => (current.length < 8 ? current + '0' : current))}
                        className="numeric h-touch-lg rounded-control border border-line-strong bg-surface text-xl font-semibold text-ink transition-colors hover:bg-surface-sunken disabled:opacity-50"
                      >
                        0
                      </button>

                      <button
                        type="button"
                        onClick={() => setPin((current) => current.slice(0, -1))}
                        disabled={pinSubmitting}
                        className="flex h-touch-lg items-center justify-center rounded-control border border-line-strong bg-surface text-ink-muted hover:bg-surface-sunken disabled:opacity-50"
                        aria-label="Delete last digit"
                      >
                        <Delete className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </div>

                    {pinSubmitting ? (
                      <div className="mt-4 flex justify-center text-butcher-green">
                        <Spinner size="sm" label={t.login.submitting} />
                      </div>
                    ) : null}

                    <button
                      type="button"
                      onClick={() => {
                        setSelectedUserId(null);
                        setPin('');
                        setFormError(null);
                      }}
                      className="btn-ghost mt-3 w-full"
                    >
                      {t.login.changePerson}
                    </button>
                  </>
                )}
              </div>
            ) : null}
          </div>

          {/* --- footer -------------------------------------------------- */}
          <div className="mt-6 text-center">
            <p className="numeric text-xs text-ink-muted">
              {formatLongDate(now, locale)} · {formatTime(now, locale)}
            </p>
            <p className="mt-2 text-xs font-medium text-ink-muted">{t.login.footer}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/// Would this person be allowed to open that path? Used so a remembered
/// destination never lands somebody on the refusal page straight after login.
function permissionAllowsPath(path: string, permissions: Set<string>): boolean {
  const item = NAV_ITEMS.find((navItem) => path.startsWith(navItem.path));
  return item ? permissions.has(item.permission) : false;
}
